/**
 * The standard conditions, each with **+ Add** — shown under the empty
 * conditions popover and in the editor's Conditions tab (`lib/condition-presets.ts`).
 *
 * The island renders and reports; the host writes the copy, because only the
 * host can mint the id and patch the screen.
 */

import { useState, type CSSProperties } from "react";
import type { SavedScreenerFilter } from "@qufolio/model";

import { presetAdded, presetGroups, type ConditionPreset, type PresetGate } from "../lib/condition-presets.js";

export interface ConditionPresetsBinding {
  /** The library the copies go into — what "✓ Added" is read against. */
  filters: readonly SavedScreenerFilter[];
  onAdd: (preset: ConditionPreset) => void;
  /** Which gated presets this caller is offered (`ConditionPreset.requires`). */
  allowed?: PresetGate;
}

export function ConditionPresetList({ filters, onAdd, allowed }: ConditionPresetsBinding) {
  // Collapsed by default (owner, 2026-10-26): ~25 presets in one long column
  // buried the library above them. A group opens on its own header, the way the
  // saved-conditions tree does, and the formula moved to the row's tooltip.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (category: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  const groups = presetGroups(allowed);
  return (
    <div>
      <div style={allRow}>
        <button type="button" onClick={() => setOpen(new Set(groups.map((g) => g.category)))} style={link}>
          Expand all
        </button>
        <span aria-hidden>·</span>
        <button type="button" onClick={() => setOpen(new Set())} style={link}>
          Collapse all
        </button>
      </div>
      {groups.map((group, index) => {
        const expanded = open.has(group.category);
        const added = group.presets.filter((p) => presetAdded(filters, p)).length;
        return (
          <div key={group.category}>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => toggle(group.category)}
              // A rule over every group but the first, so where one category
              // ends and the next begins reads at a glance (owner, 2026-10-26).
              style={index > 0 ? { ...groupLabel, ...ruled } : groupLabel}
            >
              <span style={{ width: "0.8rem" }}>{expanded ? "▾" : "▸"}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{group.category}</span>
              <span style={{ color: "var(--qf-text-muted)" }}>
                {added > 0 ? `${added}/${group.presets.length}` : group.presets.length}
              </span>
            </button>
            {expanded &&
              group.presets.map((preset) => {
                const isAdded = presetAdded(filters, preset);
                return (
                  <div key={preset.id} title={preset.source} style={row}>
                    <span style={name}>{preset.name}</span>
                    <button
                      type="button"
                      disabled={isAdded}
                      aria-label={isAdded ? `${preset.name} added` : `Add ${preset.name}`}
                      onClick={() => onAdd(preset)}
                      style={{ ...addButton, ...(isAdded ? addedButton : {}) }}
                    >
                      {isAdded ? "✓ Added" : "+ Add"}
                    </button>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}

const groupLabel: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.3rem",
  width: "100%",
  padding: "0.15rem 0.2rem",
  marginTop: "0.15rem",
  border: "none",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  cursor: "pointer",
};

// The saved list's row: indented under its group, one line.
const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.12rem 0.2rem 0.12rem 1rem",
  borderRadius: 3,
};

const name: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};


const addButton: CSSProperties = {
  flex: "0 0 auto",
  border: "1px solid var(--qf-accent)",
  borderRadius: 4,
  padding: "0 0.35rem",
  background: "transparent",
  color: "var(--qf-accent)",
  fontSize: "0.68rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const addedButton: CSSProperties = {
  borderColor: "var(--qf-positive)",
  color: "var(--qf-positive)",
  cursor: "default",
};

const ruled: CSSProperties = {
  borderTop: "1px solid var(--qf-border)",
  marginTop: "0.25rem",
  paddingTop: "0.3rem",
};

const allRow: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "0.35rem",
  padding: "0 0.2rem 0.15rem",
  color: "var(--qf-text-muted)",
  fontSize: "0.68rem",
};

const link: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--qf-accent)",
  cursor: "pointer",
  padding: 0,
  fontSize: "0.68rem",
};
